import React from 'react';
import { TextField, IconButton, MenuItem, SxProps, Theme } from '@mui/material';
import { Close } from '@mui/icons-material';
import { AssignedMicrophone } from 'core/models/layout';
import Autocomplete from '@mui/material/Autocomplete';
import { MicrophoneDevice, MediaDevice, makeMicrophoneDevice } from 'core/models/mediaDevice';

export type MicrophoneListProps = {
    className?: string;
    styles: {
        select: SxProps<Theme>;
        clearIndicator: SxProps<Theme>;
    };
    selected: { tag?: MediaDevice.Tag };
    options: MicrophoneDevice[] | undefined;
    unplugged: boolean;
    onChange: (selected: { tag?: MediaDevice.Tag }) => void;
}

const SimpleMicrophoneList = (props: MicrophoneListProps) => {
    const { selected: { tag }, options = [], unplugged, styles, onChange } = props;

    const candidates = unplugged ? [...options, { tag }] : options;

    function currentAssignment(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const assigned = candidates.find(c => c.tag === e.target.value as string);// ?? {};
        if (!assigned) return {};
        return { tag: assigned.tag };
    }

    return (
        <TextField
            select
            label='Microphone Slot'
            sx={styles.select}
            margin='normal'
            variant='outlined'
            fullWidth
            value={tag}
            InputProps={{
                startAdornment: tag
                    ? <IconButton
                        sx={styles.clearIndicator}
                        onClick={() => onChange({})}
                        size="large"><Close /></IconButton>
                    : undefined,
            }}
            onChange={e => onChange(currentAssignment(e))}
        >
            {
                candidates.map((x, i) =>
                    <MenuItem key={i} value={x.tag}>{x.tag}</MenuItem>)
            }
        </TextField >
    );
};

function selectedValue(candidates: MicrophoneDevice[], selected: AssignedMicrophone) {
    return makeMicrophoneDevice(
        selected.tag
            ? candidates.find(c => c.tag == selected.tag) ?? { id: '', tag: selected.tag }
            : { id: '', tag: '' }
    );
}

const AutocompleteMicrophoneList = (props: MicrophoneListProps) => {
    const { selected, options, onChange } = props;

    const candidates = options ?? [];

    return (
        <Autocomplete
            options={candidates}
            renderOption={
                (props, microphone) => {
                    return (
                        <li {...props}>
                            <div>{microphone.tag}</div>
                            <div style={{ fontSize: 'small' }}>{`(id: ${microphone.id})`}</div>
                        </li>
                    );
                }}
            getOptionLabel={option => option?.tag ?? ''}
            style={{ width: '50ex' }}
            renderInput={params => (
                <TextField
                    {...params}
                    label='Microphone Slot'
                    margin='normal'
                    variant='outlined'
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        type: 'text',
                    }}
                />)}
            onChange={(_, v) => onChange({ tag: v ? v.tag : undefined })}
            value={selectedValue(candidates, selected)}
        />
    );
};

export function MicrophoneList(props: MicrophoneListProps & { autoComplete?: boolean }) {
    const { autoComplete = false, ...otherProps } = props;
    return autoComplete ? <AutocompleteMicrophoneList {...otherProps} /> : <SimpleMicrophoneList {...otherProps} />;
}
